import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import CategoryIcon from '@mui/icons-material/Category';
import { getBreakdown, type DrillSelector } from '../../api/drilldown';

function fmt(n: number): string {
  return new Intl.NumberFormat('he-IL', { maximumFractionDigits: 0 }).format(Math.round(n));
}

export default function DrilldownModal({
  selector,
  onClose,
}: {
  selector: DrillSelector | null;
  onClose: () => void;
}) {
  const open = selector !== null;
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['drilldown', selector],
    queryFn: () => getBreakdown(selector!),
    enabled: open,
  });

  const moveCategory = () => {
    if (!selector?.category) return;
    onClose();
    navigate(`/classification?focus=${encodeURIComponent(selector.category)}`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        פירוט תנועות
        {data && (
          <Typography variant="caption" color="text.secondary" component="div">
            {data.selector_label} · {data.period_label}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <LinearProgress />}
        {error && (
          <Typography color="error">שגיאה בטעינת הפירוט</Typography>
        )}
        {data && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
              <Chip
                label={`${data.line_count} שורות`}
                color={data.line_count > 0 ? 'primary' : 'default'}
              />
              <Chip
                label={`חובה ₪${fmt(data.total_debit)}`}
                sx={{ backgroundColor: '#E3F2FD', color: '#0D47A1' }}
              />
              <Chip
                label={`זכות ₪${fmt(data.total_credit)}`}
                sx={{ backgroundColor: '#FFF3E0', color: '#E65100' }}
              />
              <Chip
                label={`נטו ₪${fmt(data.total_signed)}`}
                sx={{
                  backgroundColor: '#E8F5E9',
                  color: '#1B5E20',
                  fontWeight: 700,
                }}
              />
            </Stack>

            {data.rows.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                לא נמצאו תנועות בטווח זה.
              </Typography>
            ) : (
              <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>תאריך</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>חשבון</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>שם</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>תיאור</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>אסמ.</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        חובה
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        זכות
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.rows.map((r) => (
                      <TableRow key={r.line_id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>
                          {r.due_date}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {r.account_key}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{r.account_name ?? '—'}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{r.description ?? ''}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: '#666' }}>
                          {r.reference ?? ''}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontFamily: 'monospace', color: r.debit_credit === 1 ? '#0D47A1' : 'inherit' }}
                        >
                          {r.debit_credit === 1 ? `ח ₪${fmt(r.amount)}` : ''}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontFamily: 'monospace', color: r.debit_credit === 2 ? '#E65100' : 'inherit' }}
                        >
                          {r.debit_credit === 2 ? `ז ₪${fmt(r.amount)}` : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {selector?.category && (
          <Button
            startIcon={<CategoryIcon />}
            onClick={moveCategory}
            color="primary"
            variant="outlined"
            sx={{ marginInlineEnd: 'auto' }}
          >
            שייך מחדש לקטגוריה אחרת
          </Button>
        )}
        <Button onClick={onClose}>סגור</Button>
      </DialogActions>
    </Dialog>
  );
}
